import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, MapPin, Calendar as CalendarIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState, SkeletonLoader } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";

/**
 * /people (SPEC 1.2.9) — card grid of every Person row. Threads are the
 * dynamic surface; People are the static one. Each card shows display
 * name, relationship, location, and last contact date when available.
 */

type PersonRow = {
  id: string;
  display_name: string;
  full_name: string | null;
  relationship_kind: string;
  relationship_specific: string | null;
  current_location: string | null;
  next_intended_contact_by: string | null;
  notes_jonas: string;
  updated_at: string;
};

export function People() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery<PersonRow[]>({
    queryKey: ["people_list", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      // gen-types hasn't run yet against the new schema; cast through unknown
      // until the next gen-types pass.
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (
              col: string,
              v: string,
            ) => Promise<{ data: PersonRow[] | null; error: unknown }>;
          };
        };
      };
      const { data, error } = await client
        .from("person")
        .select(
          "id, display_name, full_name, relationship_kind, relationship_specific, current_location, next_intended_contact_by, notes_jonas, updated_at",
        )
        .eq("user_id", userId);
      if (error) throw error as Error;
      const rows = (data ?? []).slice();
      rows.sort((a, b) => a.display_name.localeCompare(b.display_name));
      return rows;
    },
  });

  return (
    <article aria-labelledby="people-heading">
      <header
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          marginBottom: "var(--space-6)",
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            People
          </p>
          <h1
            id="people-heading"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-m)",
              margin: "var(--space-2) 0 0",
            }}
          >
            Who's in the loop.
          </h1>
        </div>
        <Link
          to="/people/new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            background: "var(--accent)",
            color: "#0a0b0d",
            fontWeight: 500,
            fontSize: "var(--type-body-s)",
            borderRadius: "var(--radius-card)",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <Plus size={14} strokeWidth={1.8} /> Add person
        </Link>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={3} height={104} />
      ) : !q.data || q.data.length === 0 ? (
        <EmptyState
          headline="No people yet."
          sub="Add the first person to start tracking threads with them."
        />
      ) : (
        <ul
          className="haus-people-grid"
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: "var(--space-3)",
            gridTemplateColumns: "repeat(2, 1fr)",
          }}
        >
          <style>{`@media (max-width: 767px) { .haus-people-grid { grid-template-columns: 1fr !important; } }`}</style>
          {q.data.map((p) => (
            <li key={p.id}>
              <PersonCard person={p} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function PersonCard({ person }: { person: PersonRow }) {
  return (
    <Link
      to={`/people/${person.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "var(--accent-wash)",
            color: "var(--accent)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-body)",
          }}
        >
          {person.display_name.slice(0, 1).toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "var(--type-body)",
              fontWeight: 500,
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {person.display_name}
          </div>
          <div
            style={{
              fontSize: "var(--type-meta)",
              color: "var(--fg-3)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {person.relationship_specific ?? person.relationship_kind}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: "var(--space-3)",
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          flexWrap: "wrap",
        }}
      >
        {person.current_location && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <MapPin size={12} strokeWidth={1.5} /> {person.current_location}
          </span>
        )}
        {person.next_intended_contact_by && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <CalendarIcon size={12} strokeWidth={1.5} />
            next by {formatShortDate(person.next_intended_contact_by)}
          </span>
        )}
      </div>
    </Link>
  );
}
